<template>
  <v-container fluid class="pa-0">
    <v-row class="d-flex justify-center">
      <v-col cols="12" class="pb-0">
        <MediaModeForm v-if="(currentUser.clientType === 'Media' || currentUser.clientType === 'Driblab')" :fields="mediaModeFields"
        :mediaMode="mediaMode" @mediaModeChange="onMediaModeChange($event)"
        :mediaModeData="mediaModeData" @mediaModeDataChange="onMediaModeDataChange($event)"
        :mediaModeFlags="mediaModeFlags" @mediaModeFlagsChange="mediaModeFlags = $event" selectorMode></MediaModeForm>
        <v-card elevation="0" outlined>
          <v-card-actions class="pb-0 pt-3">
            <v-container fluid class="pa-0">
              <v-row id="ranking-data">
                <v-col class="d-flex flex-column pb-1 align-start pl-4 pt-0"
                  cols="12"
                  sm="4" >
                  <v-card-title class="pa-0 header-font" style="font-size: 22px;">{{ $t('general.filters') }}</v-card-title>
                  <div
                    class="mr-5 pt-0 mt-0 primary--text font-weight-bold body-1"
                  >
                    {{ $t('table.clientLeagues.seasons') }}
                    <v-skeleton-loader v-if="loading" type="text" class="mt-2" width="200"></v-skeleton-loader>
                  </div>
                  <v-autocomplete class="w-90" hide-details dense clearable 
                    v-if="!loading" 
                    :items="playerSeasonDataFiltered" 
                    v-model="seasonModel" 
                    item-value="seasonId" 
                    item-text="compoundText"
                    @change="onPlayerSeasonChange($event)" 
                  >
                    <template v-slot:selection="{item}">
                      <v-chip small color="lightSecondary lighten-2" class="body-2 mb-1 w-90 black--text">
                        <img height="20" class="mr-2"
                          :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/${item.leagueId}.png`"
                          @error="onImageLoadFailure($event)" />
                        <span class="body-2">{{item.name}}</span>
                        <span class="font-weight-bold">{{item.seasonName}}</span>
                        <span class="mx-1">@</span>
                        <span class="font-weight-medium">{{item.teamName}}</span>
                      </v-chip>
                    </template>
                    <template v-slot:item="{item}">
                      <v-card-text
                        class="pa-0 primary--text body-2 font-weight-medium d-flex align-center">
                        <img height="20" class="mr-2" contain
                        :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/${item.leagueId}.png`"
                        @error="onImageLoadFailure($event)">
                        <span class="d-flex flex-column">
                          {{item.seasonName}}
                          <span>{{item.teamName}}</span>
                        </span>
                      </v-card-text>
                    </template>
                  </v-autocomplete>
                  <v-skeleton-loader v-else type="text" class="mt-2" width="200"></v-skeleton-loader>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  class="pl-4 pt-6"
                  v-if="!$vuetify.breakpoint.smAndDown || showOptions"
                >
                  <div
                    class="header-font"
                    style="font-size: 22px;"
                  >{{$t('beeswarm.template')}}
                    <v-btn small @click="onNewTemplateClick()" rounded class="elevation-0 monospace-font white--text px-2" color="secondary">
                      <v-icon color="white" small>add</v-icon>{{$t("general.new")}}
                    </v-btn>
                    <v-skeleton-loader v-if="loading" type="paragraph@2,text@2" class="mt-2"></v-skeleton-loader>
                  </div>
                  <v-radio-group
                    v-if="!loading"
                    @change="onTemplateChange()"
                    v-model="templateActiveId"
                    dense
                    hide-details
                    class="mt-0 d-flex flex-column"
                  >
                    <div
                      v-for="(group, groupKey) in groupedTemplates"
                      :key="groupKey"
                      class="mb-4"
                    >
                      <div class="mb-2 primary--text font-weight-bold subheader-font body-1">
                        <v-icon class="me-1" :style="`font-size: ${getRadarTemplateSubtitle(groupKey).size}`">
                          {{ getRadarTemplateSubtitle(groupKey).icon }}
                        </v-icon>
                        {{ getRadarTemplateSubtitle(groupKey).label }}
                      </div>
                      <v-row dense no-gutters wrap class="d-flex flex-row">
                        <v-col
                          v-for="template in group"
                          :key="template.radarId"
                          cols="auto"
                        >
                          <v-radio class="me-2 custom-radio"
                            :value="template.radarId"
                            color="secondary"
                            :disabled="(xyTemplateDisabled && template.cXY) || (physicalTemplateDisabled && template.cPD) || (arrigoTemplateDisabled && template.cA)"
                            :style="getRadioStyle(template)"
                          >
                            <template v-slot:label>
                              <span v-if="player.isMale" class="body-1 primary--text font-weight-medium">
                                {{ template.radarName ? template.radarName : $t(`playerRecruitment.radarTemplates.${template.code}`) }}
                              </span>
                              <span v-else class="body-1 primary--text font-weight-medium">
                                {{ template.radarName ? template.radarName : $t(`playerRecruitment.radarTemplatesWomen.${template.code}`) }}
                              </span>
                              <span class="ml-1 body-2" v-if="hasCustomRadars && template.clientId === 0">(Driblab)</span>
                            </template>
                          </v-radio>
                        </v-col>
                      </v-row>
                    </div>
                  </v-radio-group>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  class="pl-4 pt-0 pb-2"
                  v-if="!$vuetify.breakpoint.smAndDown || showOptions"
                >
                  <v-card-title class="pa-0 header-font" style="font-size: 22px;">{{ $t('beeswarm.compareWith') }}</v-card-title>
                  <div class="d-flex flex-column">
                    <div class="d-flex flex-column">
                      <div class="d-flex">
                        <div class="mr-5 primary--text font-weight-bold body-1"
                        >{{$t('general.position')}}
                        </div>                     
                      </div>
                      <v-skeleton-loader v-if="loading" type="text@3" class="mt-2"></v-skeleton-loader>
                      <div v-if="!loading" class="d-flex flex-wrap">
                        <v-checkbox 
                          v-for="(shortPosition,index) in allShortPositions"
                          :key="index"
                          v-model="shortPositionsPlayedModel"
                          :value="shortPosition.fieldPositionId"
                          dense
                          hide-details
                          color="secondary"
                          class="w-30 mr-1 mt-0 py-1 body-2"
                          @change="onChangeInput(false)"
                          :disabled="shortPositionsPlayedModel.length >= 6 && !shortPositionsPlayedModel.includes(shortPosition.fieldPositionId)"
                        >
                          <template v-slot:label>
                            <div class="d-flex flex-column">
                              <span class="body-1 primary--text font-weight-medium">
                                {{`${ getPositionCode(shortPosition.fieldPositionId)}`}} 
                                <span v-if="shortPosition.minutesPlayed" class="body-2 primary--text">
                                  {{ `(${shortPosition.minutesPlayed}')`}}
                                </span>
                              </span>
                              </div>
                          </template>
                        </v-checkbox>
                      </div>
                      <div :class="shortPositionsPlayedModel.length == 6 ? 'font-weight-bold red--text' : ''">
                        {{ shortPositionsPlayedModel.length }} {{ $t('game.gamePossessionMap.of') }} {{ 6 }} {{ $t('general.maximum') }}
                      </div>
                    </div>
                    <p class="mt-2 mb-0 primary--text font-weight-bold body-1">{{$t('table.clientLeagues.seasons')}}</p>
                    <v-autocomplete
                      :items="seasonsClientFiltered"
                      v-model="seasonsActiveObjFiltered"
                      item-text="name"
                      item-value="id"
                      multiple
                      hide-details
                      class="mt-0 pt-0 w-90"
                      return-object
                      clearable
                      @blur="onBlurSeasonInput($event)"
                      @change="onChangeSeasonInput($event)"
                    >
                      <template slot="selection" slot-scope="{ item, index }">
                        <v-chip v-if="index === 0" small color="lightSecondary lighten-2">
                          <object :data="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/${item.lId}.png`"  type="image/png" height="20" class="mr-2">
                            <img :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png`" height="20">
                          </object>
                          <span class="body-2 black--text">&nbsp;{{ item.name }}</span>
                        </v-chip>
                        <span
                          v-if="index === 1"
                          class="primary--text body-2 subheader-font"
                        >(+{{ seasonsActiveObjFiltered.length - 1 }} {{ $t('finishingMap.more') }})</span>
                      </template>
                      <template v-slot:item="data">
                          <v-list-item-avatar class="d-flex align-center">
                            <v-icon v-if="data.item.seasonIds">list</v-icon>
                            <object v-else :data="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/${data.item.lId}.png`"  type="image/png" height="20" class="mr-2">
                              <img :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png`" height="20">
                            </object>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title v-html="data.item.name"></v-list-item-title>
                          </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </div>
                </v-col>
              </v-row>
              <v-divider class="my-1"></v-divider>
              <v-row id="basic-filters" v-if="!$vuetify.breakpoint.smAndDown || showOptions">
                <v-col class="d-flex px-3" cols="12" sm="3">
                  <v-range-slider
                    label="Minutes"
                    v-model="minutes"
                    max="5000"
                    min="0"
                    step="50"
                    hide-details
                    :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                    class="align-center px-1 py-0"
                    @change="onChangeInput(false)"
                  >
                    <template slot="label">
                      <div class="d-flex flex-column">
                        <span
                          class="primary--text font-weight-bold body-1"
                        >{{$t('playerScout.minutes')}}</span>
                        <span class="body-2 subheader-font monospace-font">
                          ({{minutes[0]}}-{{minutes[1]}})
                        </span>
                      </div>
                    </template>
                    <template v-slot:thumb-label="props">
                        {{ props.value }}
                    </template>
                  </v-range-slider>
                </v-col>
                <v-col class="d-flex px-3" cols="12" sm="3">
                  <v-range-slider
                    label="Age"
                    v-model="age"
                    max="50"
                    min="0"
                    step="1"
                    hide-details
                    :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                    class="align-center px-1 py-0"
                    @change="onChangeInput(false)"
                  >
                    <template slot="label">
                      <div class="d-flex flex-column">
                        <div
                          class="primary--text font-weight-bold body-1"
                        >{{$t('playerScout.age')}}</div>
                        <div class="body-2 subheader-font monospace-font">
                          ({{age[0]}}-{{age[1]}})
                        </div>
                      </div>
                    </template>
                    <template v-slot:thumb-label="props">
                        {{ props.value }}
                    </template>
                  </v-range-slider>
                </v-col>
                <v-col class="d-flex px-3" cols="12" sm="3">
                  <v-range-slider
                    label="Height"
                    v-model="height"
                    max="210"
                    min="120"
                    step="1"
                    hide-details
                    :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                    class="align-center px-1 py-0"
                    @change="onChangeInput(false)"
                  >
                    <template slot="label">
                      <div class="d-flex flex-column">
                        <div
                          class="primary--text font-weight-bold body-1"
                        >{{$t('playerScout.height')}}</div>
                        <div class="body-2 subheader-font monospace-font">
                          ({{height[0]}}-{{height[1]}})
                        </div>
                      </div>
                    </template>
                    <template v-slot:thumb-label="props">
                        {{ props.value }}
                    </template>
                  </v-range-slider>
                </v-col>
                <v-col class="d-flex px-3" cols="12" sm="3">
                  <v-checkbox 
                    v-model="showZeros"
                    dense
                    hide-details
                    color="secondary"
                    class="mr-1 mt-0 py-1 body-2"
                    @change="onChangeInput(false)"
                  >
                    <template v-slot:label>
                      <div class="d-flex flex-column">
                        <div
                          class="primary--text font-weight-bold body-1"
                        >Show Zeros</div>
                      </div>
                      <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                        <template #activator="{on}">
                          <v-icon 
                            v-on="on" 
                            color="secondary" 
                            small
                            class="pl-1"
                          >info</v-icon>
                        </template>
                        <div class="d-flex flex-column align-center">
                          <span class="black--text body-1">{{ $t('beeswarm.warningZeros') }}</span>
                        </div>
                      </v-tooltip>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row id="highlight-filters" v-if="!$vuetify.breakpoint.smAndDown || showOptions">
                <!--Highlight players of-->
                <v-col v-if="teamsOnBeeswarm" cols="12" md="3" class="py-0 px-5 d-flex flex-column align-start mt-2">
                  <v-card-title class="pa-0 mb-0 body-1 font-weight-bold primary--text flex-shrink-0">{{ $t('beeswarm.highlightPlayersOf') }}</v-card-title>
                  <v-card-text class="pa-0 flex-shrink-1">
                    <v-autocomplete class="pa-0 body-2 mb-2" multiple hide-details clearable
                      :items="teamsOnBeeswarm"
                      v-model="teamsToHighlightPlayersModel"
                      item-text="name"
                      item-value="id"
                      :placeholder="$t('searchbar.teams')"
                      @change="onChangeInput(false)"
                    >
                      <template v-slot:selection="{item, index}">
                        <v-chip v-if="index < 1" small color="lightSecondary lighten-2" class="body-1 mb-1">
                          <img height="16" class="mr-2" contain
                            :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                            @error="onImageLoadFailure($event)">
                          <span class="body-2">{{ item.name }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="primary--text body-2 subheader-font">(+{{ teamsToHighlightPlayersModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                      </template>
                      <template v-slot:item="{item}">
                        <img height="16" class="mr-2" contain
                          :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                          @error="onImageLoadFailure($event)">
                        <span class="body-2">{{ item.name }}</span>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-col>
                <!--Highlight players-->
                <v-col v-if="playersOnBeeswarm" cols="12" md="3" class="py-0 px-5 d-flex flex-column align-start mt-2">
                  <v-card-title class="pa-0 mb-0 body-1 font-weight-bold primary--text flex-shrink-0">{{ $t('beeswarm.highlightPlayers') }}</v-card-title>
                  <v-card-text class="pa-0 flex-shrink-1">
                    <v-autocomplete class="pa-0 body-2 mb-2" multiple hide-details clearable
                      :items="playersOnBeeswarm"
                      v-model="playersToHighlightModel"
                      item-text="name"
                      item-value="id"
                      :placeholder="$t('searchbar.players')"
                      @change="onChangeInput(false)"
                    >
                      <template v-slot:selection="{item, index}">
                        <v-chip v-if="index < 1" small color="lightSecondary lighten-2" class="body-1 mb-1">
                          <img height="16" class="mr-2" contain
                            :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                            @error="onImageLoadFailure($event)">
                          <span class="body-2">{{ item.name }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="primary--text body-2 subheader-font">(+{{ playersToHighlightModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                      </template>
                      <template v-slot:item="{item}">
                        <span class="mr-3">{{ item.name }}</span>
                          <div class="d-flex align-center mt-1">
                            <img contain width="12" @error="onImageLoadFailure($event)"
                              :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.teamId + '.png'"
                            />
                            <span class="ml-1 body-2">{{ item.tN }}</span>
                          </div>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-col>
                <!--Exclude Teams-->
                <v-col v-if="teamsOnBeeswarm" cols="12" md="3" class="py-0 px-5 d-flex flex-column align-start mt-2">
                  <v-card-title class="pa-0 mb-0 body-1 font-weight-bold primary--text flex-shrink-0">{{ $t('playerScout.excludeTeams') }}</v-card-title>
                  <v-card-text class="pa-0 flex-shrink-1">
                    <v-autocomplete class="pa-0 body-2 mb-2" multiple hide-details clearable
                      :items="teamsOnBeeswarm"
                      v-model="excludeTeamsModel"
                      item-text="name"
                      item-value="id"
                      :placeholder="$t('searchbar.teams')"
                      @change="onExcludeTeamsClick(); onChangeInput(false)"
                    >
                      <v-list-item slot="prepend-item" ripple @click="onExcludedSelectAllClick()">
                        <v-list-item-content>
                          <v-list-item-title>{{ $t('finishingMap.selectAll') }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <template v-slot:selection="{item, index}">
                        <v-chip v-if="index < 1" small color="lightSecondary lighten-2" class="body-1 mb-1">
                          <img height="16" class="mr-2" contain
                            :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                            @error="onImageLoadFailure($event)">
                          <span class="body-2">{{ item.name }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="primary--text body-2 subheader-font">(+{{ excludeTeamsModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                      </template>
                      <template v-slot:item="{item}">
                        <img height="16" class="mr-2" contain
                          :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                          @error="onImageLoadFailure($event)">
                        <span class="body-2">{{ item.name }}</span>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-col>
                <!--Filter teams-->
                <v-col cols="12" md="3" class="py-0 px-5 d-flex flex-column align-start mt-2">
                  <v-card-title class="pa-0 mb-0 body-1 font-weight-bold primary--text flex-shrink-0">{{ $t("beeswarm.filterTeams") }}</v-card-title>
                  <v-card-text class="pa-0 flex-shrink-1">
                    <v-autocomplete class="pa-0 body-2 mb-2" multiple hide-details clearable
                      :items="teamsOnBeeswarm"
                      v-model="filterTeamsModel"
                      item-text="name"
                      item-value="id"
                      :placeholder="$t('searchbar.teams')"
                      @change="onFilterTeamsClick(); onChangeInput(false)"
                    >
                      <v-list-item slot="prepend-item" ripple @click="onFilterSelectAllClick()">
                        <v-list-item-content>
                          <v-list-item-title>{{ $t('finishingMap.selectAll') }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <template v-slot:selection="{item, index}">
                        <v-chip v-if="index < 1" small color="lightSecondary lighten-2" class="body-1 mb-1">
                          <img height="16" class="mr-2" contain
                            :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                            @error="onImageLoadFailure($event)">
                          <span class="body-2">{{ item.name }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="primary--text body-2 subheader-font">(+{{ filterTeamsModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                      </template>
                      <template v-slot:item="{item}">
                        <img height="16" class="mr-2" contain
                          :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.id}.png`"
                          @error="onImageLoadFailure($event)">
                        <span class="body-2">{{ item.name }}</span>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-row id="download-zone">
                <v-col class="d-flex justify-end py-2 px-2" cols="12" >
                  <v-btn
                    color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small :x-small="!$vuetify.breakpoint.xsOnly"
                    v-on:click.native="saveAsPNG()"
                  >
                    <v-icon small class="mr-1">
                      file_download</v-icon
                    >
                    <span class="">PNG</span>
                  </v-btn>
                  <v-btn
                    color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small :x-small="!$vuetify.breakpoint.xsOnly"
                    v-on:click.native="saveAsSVG()"
                  >
                    <v-icon small
                      >file_download</v-icon
                    >
                    <span>SVG</span>
                  </v-btn>
                  <v-btn
                    color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small :x-small="!$vuetify.breakpoint.xsOnly"
                    :loading="loadingExport"
                    :disabled="!beeswarmLoaded"
                    v-on:click.native="onGenerateIFrameClick()"
                  >
                    <v-icon small class="mr-1"
                      >share</v-icon
                    >
                    <span>Embed</span>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="d-flex justify-center" cols="12" v-if="$vuetify.breakpoint.smAndDown">
                  <v-btn color="primary" small outlined rounded @click="showOptions = !showOptions" class="px-4">
                    <span
                      class="monospace-font body-1 primary--text"
                    >{{$t(showOptions ? 'general.lessOptions' : "general.moreOptions")}}</span>
                    <v-icon right>{{ showOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- Category buttons -->
      <v-col id="category-buttons" v-if="!mediaMode && categoriesOfRadar.length" cols="12" class="d-flex px-3 pb-0 justify-center">
        <div class="d-flex flex-column align-center">
          <v-chip
            class="mr-2 my-1 d-flex justify-center"
            color="secondary"
            :outlined="!allCategoriesSelected"
            :label="true"
            small
            @click="onAllCategoriesClick()"
          >
            <span class="body-2 font-weight-medium monospace-font">{{$t('general.all')}}</span>
          </v-chip>
        </div>
        <v-divider vertical></v-divider>
        <div class="d-flex flex-column align-center">
          <div class="d-flex px-2 flex-wrap">
            <v-chip
              v-for="(category) in categoriesOfRadar"
              :key="category.id"
              :outlined="!isHeaderActive(category)"
              class="mr-2 my-1 d-flex justify-center"
              color="secondary"
              :label="true"
              small
              @click="onCategoryClick(category)"
            >
              <span class="body-2 font-weight-medium monospace-font">{{ category.code }} ({{ category.count }})</span>
            </v-chip>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="12" ref="beeswarm" v-bind:style="{'max-width':this.mediaMode ? '1185px' : ''}">
        <v-row v-if="!loading && !mediaMode && beeswarmDataFiltered.bees.length > 0"  class="mx-1">
          <v-col v-if="beeswarmDataFiltered.stats.length > 0"
            cols="12"
            md="6"
            class="text-start py-0 px-sm-3 mb-4 mt-1"
            v-for="(stat, index) in beeswarmDataFiltered.stats"
            :key="index"
          >
            <v-tooltip bottom color="lightSecondary lighten-4" max-width="300" v-if="stat.name">
              <template v-slot:activator="{ on, attrs }">
                <div>
                  <p class="font-weight-bold mb-0" style="font-size: 20px;" v-on="on" v-bind="attrs">
                    <v-icon v-if="stat.product && stat.product.icon" :style="stat.product.icon.startsWith('fa-') ? 'font-size: 14px' : 'font-size: 18px'" color="primary">{{ stat.product.icon }}</v-icon>
                    {{ stat.name }}
                  </p>
                  <p class="body-1 subheader-font primary--text mb-0">{{ stat.subtitle }}</p>
                </div>
              </template>
              <div class="d-flex flex-column black--text body-1">
                <span class="font-weight-bold">{{ stat.name }}</span>
                <span class="monospace-font body-2">{{ stat.product.name }}</span>
                <span v-html="stat.tooltip" v-if="stat.name !== stat.tooltip"></span>
              </div>
            </v-tooltip>
            <Beeswarm
              v-if="size != 0 && seasonsActiveFiltered.length > 0"
              ref="beeswarmRef"
              :index="index"
              :size="size"
              :chartData="beeswarmDataFiltered.bees"
              :playerId="parseInt(playerId)"
              :seasonId="seasonModel"
              :teamId="seasonModelObj.teamId"
              :statId="stat.id"
              :stat="stat.code"
              :reversedStat="stat.r"
              :highlightPlayersOfTeamId="teamsToHighlightPlayersModel"
              :highlightPlayersByPlayerId="playersToHighlightModel"
              :excludeTeams="excludeTeamsModel"
              :idKey="idKey"
              :key="idKey"
              :darkMode="darkMode"
              :parentSVG="$refs.parentSVGRef"
              :bgColor="beeswarmDefaultColors.bgColor"
              :textColor="beeswarmDefaultColors.textColor"
              :axisColor="beeswarmDefaultColors.axisColor"
              :doTimeOut="doTimeOut"
              :showZeros="showZeros"
              @dataLoaded="onBeeswarmDataLoaded($event, stat.code)"
              @circleOver="onCircleOver($event)"
              @circleOut="onCircleOut($event)"
            />
          </v-col>
        </v-row>
        <v-row v-if="!loading && mediaMode && beeswarmDataFiltered.bees.length > 0" class="mx-1">
          <v-col cols="12" class="text-center px-0 py-0" v-if="player && player.name">
            <PlayerAvatarMedia :player="player" :width="mediaModeData.comparisonPlayer ? avatarSize.width / 1.5 : avatarSize.width" :height="$vuetify.breakpoint.lgAndUp ? avatarSize.height : avatarSize.height * 0.44" :id="player.playerId"
              :title="mediaModeData.upperText" :subtitle="mediaModeData.lowerText"
              :color="mediaModeData.borderColor" ref="playerAvatarMediaRef"
              :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
              :face="mediaModeData.face ? mediaModeData.face.dataUrl : ''"
              :nationalTeam="mediaModeData.nationalTeam"
              :style="`background:${mediaModeData.bgColor}`"
              :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize"
              :lineWeight="mediaModeData.lineWeight"
              :comparisonFace="mediaModeData.comparisonFace ? mediaModeData.comparisonFace.dataUrl : ''"
              :comparisonPlayerData="mediaModeData.comparisonPlayer" :comparisonTitle="mediaModeData.comparisonUpperText" :comparisonSubtitle="mediaModeData.comparisonLowerText" :comparisonColor="mediaModeData.comparisonBorderColor"
              :upperTextFontSize="mediaModeData.upperTextFontSize" :upperTextFontWeight="mediaModeData.upperTextFontWeight"
              :lowerTextFontSize="mediaModeData.lowerTextFontSize" :lowerTextFontWeight="mediaModeData.lowerTextFontWeight"
              :fontFamily="mediaModeData.fontFamily"
            />
          </v-col>
          <v-col
            cols="12"
            class="text-center py-0 px-0"
            v-for="(stat, index) in beeswarmDataFiltered.stats"
            :key="index"
          >
            <v-tooltip bottom color="lightSecondary lighten-4" max-width="300" v-if="stat.name">
              <template v-slot:activator="{ on, attrs }">
                <p class="font-weight-bold mb-0" v-on="on" v-bind="attrs" 
                  :style="{fontSize: '20px', backgroundColor: mediaModeData.bgColor, textColor: mediaMode.textColor}">
                  <v-icon v-if="stat.product && stat.product.icon" :style="stat.product.icon.startsWith('fa-') ? 'font-size: 14px' : 'font-size: 18px'" color="primary">{{ stat.product.icon }}</v-icon>
                  {{ stat.name }}
                </p>
                <p class="mb-0" :style="{fontSize: '14px', backgroundColor: mediaModeData.bgColor, textColor: mediaMode.textColor}">{{ stat.subtitle }}</p>
              </template>
              <div class="d-flex flex-column black--text body-1">
                <span class="font-weight-bold">{{ stat.name }}</span>
                <span class="monospace-font body-2">{{ stat.product.name }}</span>
                <span v-html="stat.tooltip" v-if="stat.name !== stat.tooltip"></span>
              </div>
            </v-tooltip>
            <Beeswarm
              v-if="size != 0 && seasonsActiveFiltered.length > 0"
              ref="beeswarmRef"
              :index="index"
              :size="$vuetify.breakpoint.lgAndUp ? size * 1.155 : size * 0.5"
              :chartData="beeswarmDataFiltered.bees"
              :playerId="parseInt(playerId)"
              :seasonId="seasonModel"
              :teamId="seasonModelObj.teamId"
              :statId="stat.id"
              :stat="stat.code"
              :reversedStat="stat.r"
              :highlightPlayersOfTeamId="teamsToHighlightPlayersModel"
              :highlightPlayersByPlayerId="playersToHighlightModel"
              :excludeTeams="excludeTeamsModel"
              :idKey="idKey"
              :darkMode="darkMode"
              :parentSVG="$refs.parentSVGRef"
              :bgColor="mediaModeData.bgColor"
              :textColor="mediaModeData.textColor"
              :axisColor="mediaModeData.axisColor"
              :comparisonPlayer="mediaModeData.comparisonPlayer"
              :dotColor="mediaModeData.borderColor"
              :comparisonDotColor="mediaModeData.comparisonBorderColor"
              :mediaMode="mediaMode"
              :doTimeOut="doTimeOut"
              :fontFamily="mediaModeData.fontFamily"
              :showZeros="showZeros"
              @sizeChange="onBeeswarmSizeChange($event)"
              @dataLoaded="onBeeswarmDataLoaded($event, stat.code)"
              @circleOver="onCircleOver($event)"
              @circleOut="onCircleOut($event)"
              logoCfg="none"
            />
          </v-col>
          <v-col cols="12" class="py-0 text-center" v-if="player && avatarSize && chartSize && mediaMode && mediaModeData.socialMedia" :style="`background-color:${mediaModeData.bgColor}`">
            <DriblabSocialMedia :width="chartSize.width" :height="50" :id="player.playerId"
            ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
            :fontSize="mediaModeData.socialMediaFontSize" :fontWeight="mediaModeData.socialMediaFontWeight"
            :logoCfg="mediaModeData.logo" type="beeswarm"
            :sponsor="mediaModeData.sponsor.dataUrl" :fontFamily="mediaModeData.fontFamily"></DriblabSocialMedia>
          </v-col>
        </v-row>
        <v-row v-if="loading">
          <v-col cols="12" class="text-center d-flex align-center justify-center">
            <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <RadarTemplateFormModal
      ref="formModalRef"
      :title="this.$t('settings.radarTemplates')"
      :subtitle="this.$t('settings.radarTemplatesSubtitle')"
      :positionId="player.positionId"
      @save="loadTemplates()"
    />
    <ChartExportLinksModal ref="chartExportLinksModalRef" :title="player.name" subtitle="Beeswarm"></ChartExportLinksModal>
    <Tour v-if="currentUser.showTour" name="playerBeeswarmTour" :steps="tourSteps" />
  </v-container>
</template>

<script>
import * as d3v4 from "d3v4";
import * as moment from 'moment';
//Components
import Beeswarm from "../base/Beeswarm";
import DriblabCountryFlag from "../base/DriblabCountryFlag";
import MediaModeForm from "../base/MediaModeForm";
import DriblabSocialMedia from "../base/DriblabSocialMedia";
import ChartExportLinksModal from "../base/ChartExportLinksModal";
import PlayerAvatarMedia from './PlayerAvatarMedia';
import RadarTemplateFormModal from "../modals/RadarTemplateFormModal";
import Tour from "../base/Tour";
import {downloadPng, exportSvg, copyAndAppendElement, loadFonts} from "../../utils/exportToPNG";
import {getNeutralColors, userHasRole, getPositionCode, getSupervisedItems, getProductInfoById, 
  getShortPositionsByTemplate, getRadarTemplateSubtitle, getRadioStyle } from "../../utils/functions";
import { v4 } from 'uuid';
//Services
import BeeswarmService from "../../services/beeswarm.service";
import StatService from "../../services/stat.service";
import PlayerService from "../../services/player/player.service";
import ClientService from "../../services/client.service";
import UserChartService from "../../services/user-chart.service";
import DocumentService from "../../services/document.service";
import UserService from '../../services/user.service';
import PlayerRadarService from "../../services/player/playerRadar.service";

const NODE_ENV = process.env.NODE_ENV;
const PROTOCOL = process.env.PROTOCOL || 'http://';
const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
const END_POINT_PORT = process.env.END_POINT_PORT || '8081';

const API_URL = NODE_ENV === 'production' ? 'https://pro.driblab.com/embed-chart/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/embed-chart/';

export default {
  name: "PlayerBeeswarm",
  components: {
    Beeswarm,
    DriblabCountryFlag,
    MediaModeForm,
    PlayerAvatarMedia,
    DriblabSocialMedia,
    ChartExportLinksModal,
    Tour,
    RadarTemplateFormModal
  },
  props:{
    playerId:{
      type: Number,
      required: false
    },
    headerPlayer:{
      type: Object,
      required: false
    },
    headerPlayerSeasonData:{
      type: Array,
      required: false
    }
  },
  watch: {
    "mediaMode": function(newVal, oldVal) {
      if(!this.mediaMode){
        this.mediaModeData = {fontFamily: 'NeueMontreal', borderColor:"#3366FF",fillColor:"#6d7de5",bgColor:"#ffffff0",upperText:this.player.name,lowerText:this.$t("playerDetail.beeswarm"),
        socialMedia:true,nationalTeam: false,textColor:"#000000",axisColor:"#000000",logo:"color",
                      face:{},stats: this.beeswarmDataFiltered.stats.map(s => s.code), socialMediaFontSize:"13",socialMediaFontWeight:"400",sponsor: {}}
      }
      this.mediaModeData.bgColor = this.mediaMode ? '#ffffff' : '#ffffff0';
      this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
      this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      this.onWindowResize()
    },
    "mediaModeData.comparisonPlayer": function(newVal,oldVal){
      if(newVal){
        this.mediaModeData.comparisonUpperText = this.mediaModeData.comparisonPlayer.name;
        this.mediaModeData.comparisonUpperText = this.mediaModeData.comparisonPlayer.name;
        this.mediaModeData.comparisonLowerText = this.$t("playerDetail.beeswarm");
        this.mediaModeData.comparisonFace.src = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/${this.mediaModeData.comparisonPlayer.id}.png`;
        this.mediaModeData.comparisonFace.fallBackSrc = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/0_${this.headerPlayer.male === false ? 'f' : 'm'}.png`;
        this.mediaModeData.comparisonBorderColor = getNeutralColors('away')[1];
      }
    }
  },
  data() {
    return {
      darkMode: this.$store.state.auth.user.darkMode || this.$vuetify.theme.dark,
      size: 0,
      prevWindowWidth: 0,
      prevWindowHeight: 0,
      player: null,
      padding: 40,
      minutes: [450, 5000],
      age: [0, 50],
      height: [120, 210],
      showZeros: false,
      categoriesOfRadar: [],
      selectedCategories: [],
      //True when physical template is selected. Used for disabled seasons
      physicalTemplateSelected: false,
      //True when season without XY data is selected. Used for disabled XY templates
      xyTemplateDisabled: false,
      //True when season without physical data is selected. Used for disabled physical template
      physicalTemplateDisabled: false,
      //True when season without arrigo data is selected. Used for disabled radars
      arrigoTemplateDisabled: false,
      seasonModel: null,
      seasonModelObj: null,
      playerSeasonData: [],
      playerSeasonDataCurrentYear: [],
      playerSeasonDataLastYear: [],
      currentYear: null,
      positionsPlayed: [],
      allShortPositions: [],
      shortPositionsPlayed: [],
      shortPositionsPlayedModel: [],
      templates: [],
      templateActiveId: null,
      //Seasons filtered by template (Filter physical seasons)
      seasonsClientFiltered: [],
      seasonsClient: [],
      seasonsActiveFiltered: null,
      seasonsActive: null,
      loading: false,
      showOptions: false,
      mediaMode: null,
      mediaModeFlags: {borderColor:null,fillColor:null,bgColor:null,upperText:null,lowerText:null,socialMedia:null, nationalTeam: null,
        textColor: null,axisColor:null,logo:null,face: null,stats:null},
      mediaModeData: {fontFamily: 'NeueMontreal', borderColor:"#3366FF",fillColor:"#6d7de5",bgColor:"#ffffff0",upperText:null,lowerText:null,socialMedia:true,nationalTeam: false,
      textColor:"#000000",logo:"color",face: {},comparisonFace:{},stats:[],axisColor:"#000000",
      upperTextFontWeight:"900",lowerTextFontWeight:"400",socialMediaFontSize:"12",socialMediaFontWeight:"300",sponsor:{}},
      mediaModeFields: [

        {type:"text",label:"mediaMode.upperText",key:"upperText"},
        {type:"number",label:"mediaMode.upperTextFontSize",key:"upperTextFontSize"},
        {type:"radio",label:"mediaMode.upperTextFontWeight",key:"upperTextFontWeight",options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"text",label:"mediaMode.lowerText",key:"lowerText"},
        {type:"number",label:"mediaMode.lowerTextFontSize",key:"lowerTextFontSize"},
        {type:"radio",label:"mediaMode.lowerTextFontWeight",key:"lowerTextFontWeight",options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"radio",label:"mediaMode.fontFamily",key:"fontFamily",options:[{value:"NeueMontreal",label:"NeueMontreal"},{value:"NeueMontreal",label:"NeueMontreal"}]},
        {type:"select-multiple",label:"general.stats",key:"stats",options:[],itemText:"text",itemValue:"value"},
        {type:"color",label:"mediaMode.textColor",key:"textColor"},
        {type:"color",label:"mediaMode.borderColor",key:"borderColor"},
        {type:"select",label:"general.comparison",key:"comparisonPlayer",returnObject:true,itemText:"name",placeholder:"playerComparison.searchBarPlaceholder"},
        {type:"text",label:"mediaMode.comparisonUpperText",key:"comparisonUpperText",comparison:true},
        {type:"text",label:"mediaMode.comparisonLowerText",key:"comparisonLowerText",comparison:true},
        {type:"color",label:"mediaMode.comparisonBorderColor",key:"comparisonBorderColor",comparison:true},
        {type:"color",label:"mediaMode.comparisonFillColor",key:"comparisonFillColor",comparison:true},
        {type:"image",label:"mediaMode.comparisonFace",key:"comparisonFace",comparison:true},
        {type:"color",label:"mediaMode.bgColor",key:"bgColor"},
        {type:"color",label:"mediaMode.axisColor",key:"axisColor"},
        {type:"radio",label:"mediaMode.logo",key:"logo",options:[{value:"color",label:"Color"},{value:"white",label:"general.white"},{value:"none",label:"favorites.noGrouped"}]},
        {type:"number",label:"mediaMode.avatarLineLength",key:"lineLength",force:true},
        {type:"number",label:"mediaMode.avatarLineWeight",key:"lineWeight",force:true},
        {type:"number",label:"mediaMode.avatarSize",key:"avatarSize",force:true},
        {type:"image",label:"mediaMode.face",key:"face"},
        {type:"switch",label:"mediaMode.nationalTeam",key:"nationalTeam"},
        {type:"switch",label:"mediaMode.socialMedia",key:"socialMedia"},
        {type:"number",label:"mediaMode.socialMediaFontSize",key:"socialMediaFontSize",force:true},
        {type:"radio",label:"mediaMode.socialMediaFontWeight",key:"socialMediaFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"image",label:"mediaMode.sponsor",key:"sponsor",force:true,aspectRatio:16/9,imageRestriction:'none',resizable:true,stencilSize:{width:300,height:300}}
      ],
      idKey: 0,
      chartSize: null,
      beeswarmLoaded: 0,
      seasonsActiveObj: null,
      //Seasons filtered by template (Filter physical seasons)
      seasonsActiveObjFiltered: null,
      loadingExport: false,
      beeswarmDataByCatgories: [],
      beeswarmAllStatsData: [],
      teamsOnBeeswarm: [],
      teamsToHighlightPlayersModel: null,
      excludeTeamsModel: [],
      doTimeOut: false,
      playersOnBeeswarm: [],
      playersToHighlightModel: null,
      filterTeamsModel: []
    };
  },
  mounted: function() {
    //If is GK, only show GK short positions
    if(this.player.fieldPositionId === 1){
      this.allShortPositions = [{fieldPositionId: 1, position: "GK", positionId: 1}] 
    }else{
      this.allShortPositions = [
        {fieldPositionId: 4, positionId: 3},
        {fieldPositionId: 2, positionId: 2},
        {fieldPositionId: 3, positionId: 3},
        {fieldPositionId: 16, positionId: 7},
        {fieldPositionId: 5, positionId: 4},
        {fieldPositionId: 15, positionId: 7},
        {fieldPositionId: 8, positionId: 4},
        {fieldPositionId: 6, positionId: 4},
        {fieldPositionId: 7, positionId: 4},
        {fieldPositionId: 11, positionId: 5},
        {fieldPositionId: 9, positionId: 5},
        {fieldPositionId: 10, positionId: 5},
        {fieldPositionId: 13, positionId: 5},
        {fieldPositionId: 14, positionId: 6},
        {fieldPositionId: 12, positionId: 5},
      ];
    }
    
  },
  computed: {
    currentUserSeasonId(){
      let teamSeasonId = _.get(this.currentUser,'team.seasonId');
      let leagueSeasonId = _.get(this.currentUser,'league.seasonId');
      if(teamSeasonId || leagueSeasonId){
        return teamSeasonId ? teamSeasonId : leagueSeasonId
      }
      return null;
    },
    beeswarmDataFiltered() {
      let selectedCategoriesIds = this.selectedCategories.map(c => c.id);
      let dataFiltered = [];
      if(this.beeswarmAllStatsData.bees){
        dataFiltered = _.cloneDeep(this.beeswarmAllStatsData);
      }else{
        dataFiltered = _.cloneDeep(this.beeswarmDataByCatgories);
      }

      dataFiltered.bees = dataFiltered.bees.filter(b => (b.id === this.playerId && b.seasonId === this.seasonModel)
        || ((b.age == null || (b.age >= this.age[0] && b.age <= this.age[1]))
          && (b.tMP >= this.minutes[0] && b.tMP <= this.minutes[1])
          && (b.height == null || (b.height >= this.height[0] && b.height <= this.height[1]))
          && (this.shortPositionsPlayedModel.includes(b.fPI)))
      );
      this.getPlayersToHighlight(dataFiltered.bees);

      //Get player's teams. Just need iterate over bees (players) of one stat (i.e: first)
      this.teamsOnBeeswarm = _.chain(dataFiltered.bees) //[0].bees
                                    .uniqBy("teamId")
                                    .map(b => {
                                      return {id: b.teamId, name: b.tN };
                                    }).value();

      this.filterTeamsModel = this.teamsOnBeeswarm.filter(t => !this.excludeTeamsModel.includes(t.id)).map(t => t.id);
      
      if(this.mediaMode){
        let statsField = _.find(this.mediaModeFields, (f) => {
          return f.key === 'stats';
        });
        //Load 'Stats' for Media Mode select
        statsField.options = _.map(dataFiltered.stats, (s) => {
          return {
            text: this.$t(`statsNaming.${s.code}`),
            value: s.code,
            product: this.getProductInfoById(s.pId, this.currentUserLanguage)
          }
        });

        dataFiltered.stats = dataFiltered.stats.filter(s => this.mediaModeData.stats.includes(s.code));
      }else if(!this.allCategoriesSelected){
        dataFiltered.stats = dataFiltered.stats
                                .filter(s=> selectedCategoriesIds.includes(s.cD))
                                .sort((a, b) => {
                                    //Sort asc by order of the stat on the radar
                                    return a.order - b.order;
                                });
      }
      return dataFiltered;
    },
    showPhysicalTemplates(){
      //Show physical data just if some player season has physical data and user has role ROLE_PHYSICAL_DATA
      return this.playerSeasonData.some( ps => ps.hasPhysicalData) && this.userHasRole("ROLE_PHYSICAL_DATA"); 
    },
    showArrigoTemplates(){
      //Show arrigo data just if some player season has physical data and user has role ROLE_ARRIGO
      return this.playerSeasonData.some(ps => ps.hasPhysicalData) && this.userHasRole("ROLE_ARRIGO"); 
    },
    //Current Radar selected, full object
    templateActive(){
      if(this.templates.length && this.templateActiveId){
        return this.templates.find(t => t.radarId === this.templateActiveId);
      }
      return;
    },
    //Position of current radar selected
    templateActivePosition(){
      if(this.templateActive){
        return this.templateActive.position;
      }
      return;
    },
    //Filter to remove seasons not hired by the client
    playerSeasonDataFiltered() {
      let dataFiltered = _.cloneDeep(this.playerSeasonData);
      console.log(dataFiltered[0]);
      if(this.templateActive){
        dataFiltered = dataFiltered.filter(s=> this.templateActive.cXY || 
                                            (this.templateActive.cPD && s.hasPhysicalData) || 
                                            (this.templateActive.cA && s.hasArrigoData)
                                        );
      }
      return dataFiltered;
    },
    hasCustomRadars(){
      return this.currentUser.clientId !== 30 && _.some(this.templates,(t) => t.clientId === this.currentUser.clientId)
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    avatarSize(){
      return {
          width: this.$refs.beeswarm.clientWidth - (this.padding * 2),
          height: this.size / 3
        }
    },
    teamId(){
      return this.playerSeasonDataCurrentYear[0] ? this.playerSeasonDataCurrentYear[0].teamId : "";
    },
    playerName(){
      return  this.playerSeasonDataCurrentYear[0] ? this.playerSeasonDataCurrentYear[0].playerName  : "";
    },
    leagueMale(){
      return  this.playerSeasonDataCurrentYear[0] ? this.playerSeasonDataCurrentYear[0].isMale  : false;
    },
    beeswarmDefaultColors(){
      if(this.darkMode){
        return {
          textColor: "white",
          axisColor: "white",
          background: "transparent"
        }
      }else{
        return {
          textColor: "black",
          axisColor: "black",
          background: "transparent"
        }
      }
    },
    lastSearchId() {
      return this.$store.state.auth.lastSearch;
    },
    allCategoriesSelected(){
      return this.categoriesOfRadar.length === this.selectedCategories.length;
    },
    tourSteps(){
      return [
        {
          target: `#ranking-data`,
          header: {
            title: this.$t("tours.playerRanking.step1.title"),
          },
          content: this.$t("tours.playerRanking.step1.message"),
          params: {
            placement: "top",
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: `#basic-filters`,
          header: {
            title: this.$t("tours.playerRanking.step2.title"),
          },
          content: this.$t("tours.playerRanking.step2.message"),
          params: {
            placement: "top",
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: `#highlight-filters`,
          header: {
            title: this.$t("tours.playerRanking.step3.title"),
          },
          content: this.$t("tours.playerRanking.step3.message"),
          params: {
            placement: "top",
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: `#category-buttons`,
          header: {
            title: this.$t("tours.playerRanking.step4.title"),
          },
          content: this.$t("tours.playerRanking.step4.message"),
          params: {
            placement: "top",
            enableScrolling: false,
            highlight: true
          }
        },
        {
          target: `#download-zone`,
          header: {
            title: this.$t("tours.playerRadar.step5.title"),
          },
          content: this.$t("tours.playerRadar.step5.message"),
          params: {
            placement: "top",
            enableScrolling: false,
            highlight: true
          }
        },
      ];
    },
    //Check if user has only ROLE_PHYSICAL_DATA (without eventing)
    onlyPhysicalData(){
      return !this.userHasRole("ROLE_EVENTING_DATA") && this.userHasRole("ROLE_PHYSICAL_DATA");
    },
    groupedTemplates() {
      if (!this.templates || !this.templates.length) return {};
      const grouped = _.groupBy(this.templates, template => {
        if (template.cXY) return 'ROLE_EVENTING_DATA';
        if (template.cPD) return 'ROLE_PHYSICAL_DATA';
        if (template.cA) return 'ROLE_ARRIGO';
        return 'OTHER';
      });
      const orderedKeys = ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA', 'ROLE_ARRIGO', 'OTHER'];
      // Return a new object with keys in desired order
      const orderedGrouped = {};
      for (const key of orderedKeys) {
        if (grouped[key]) {
          orderedGrouped[key] = grouped[key];
        }
      }
     
      return orderedGrouped;
    }
  },
  async created() {
    window.addEventListener("resize", this.onWindowResize);
    this.loading = true;
    this.player = this.headerPlayer;
    await this.loadSeasonData();
    await this.loadSeasonsClient();
    await this.loadPositionsPlayed();
    //Get all available templates by clientId
    await this.loadTemplates();
    await this.loadStats();
    await this.loadBeeswarmData();
    this.loadAllStatsBeeswarmData();
    this.loadSeasonLists();

    //All teams selected by default
    this.filterTeamsModel = [...new Set(this.beeswarmDataFiltered.bees.map(b => b.teamId))];

    this.mediaMode = this.currentUser.mediaMode || this.currentUser.clientType === 'Media';
  },
  destroyed() {
    d3v4.selectAll('.tooltip').remove();
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    async onAllCategoriesClick() {
      this.loading = true;
      this.selectedCategories = [...this.categoriesOfRadar];
      setTimeout(() => { this.onChangeInput(true);}, 100);
    },
    isHeaderActive(category) {
      return _.find(this.selectedCategories, (sc) => sc.id === category.id);
    },
    async onCategoryClick(category) {
      this.loading = true;
      this.selectedCategories = [category];
      setTimeout(() => { this.onChangeInput(true);}, 100);

    },
    async loadStats() {
      let templateActiveObject = this.templates.find(t => t.radarId === this.templateActiveId);
      let statsResponse = await StatService.getStatsOfRadar(templateActiveObject.radarId);

      this.categoriesOfRadar = statsResponse.data
                                .map(({ categoryId, category }) =>
                                     ({ id: categoryId,
                                        code: this.$t('faq.metrics.categories.' + category),
                                        count: statsResponse.data.filter(s=> s.categoryId === categoryId).length })
                                )
                                .filter(function (c) {
                                  var key = c.id;
                                  if (!this[key]) {
                                      this[key] = true;
                                      return true;
                                  }
                                }, Object.create(null)).sort((a, b) => {
                                    //Sort to get first more stats
                                    return b.count - a.count;
                                });

      this.selectedCategories = [this.categoriesOfRadar[0]];
    },
    async loadSeasonData() {
      this.playerSeasonData = _.map(this.headerPlayerSeasonData,(h) => {h.compoundId = `${h.seasonId}-${h.teamId}`; h.compoundText = `${h.seasonName}-${h.teamName}`; return h;});
      //If user has only PHYSICAL_DATA role, then show onlye seasons with PD
      if(this.onlyPhysicalData){
        this.playerSeasonData = this.playerSeasonData.filter(psd => psd.hasPhysicalData);
      }
      // Default season : Last Regular season of the player's team
      let defaultSeason = this.playerSeasonData.find(psd => psd.regular && 
                                            psd.teamId === this.player.teamId && 
                                            psd.year === psd.leagueLastYear);

      if(!defaultSeason){
        // If there is no season, take last regular played in other team
        defaultSeason = this.playerSeasonData.find(psd => psd.regular && 
                                          psd.year === psd.leagueLastYear);
      }

      if(!defaultSeason){
        // If there is no season, take first regular from all seasons played
        defaultSeason = this.playerSeasonData.find(psd => psd.regular) || this.playerSeasonData[0];
      }

      // Get playerSeasonInfoId of default season
      this.seasonModelObj = defaultSeason;
      this.seasonModel = defaultSeason.seasonId;
      console.log(defaultSeason);
      //Disabled physical template if season has no physical data
      this.physicalTemplateDisabled = !defaultSeason.hasPhysicalData;
      this.arrigoTemplateDisabled = !defaultSeason.hasArrigoData;
      this.xyTemplateDisabled = !defaultSeason.hasXY;
      
      // Split seasons in years
      this.currentYear = this.playerSeasonData
        .map(s => s.year)
        .reduce((a, b) => (a.year > b.year ? b : a));
      this.playerSeasonDataCurrentYear = this.playerSeasonData.filter(
        s => s.year === this.currentYear
      );
      this.playerSeasonDataLastYear = this.playerSeasonData.filter(
        s => s.year !== this.currentYear
      );
      this.mediaModeData.upperText = this.player.name;
      this.mediaModeData.lowerText = this.$t("playerDetail.beeswarm");
      this.mediaModeData.face.src = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/${this.player.playerId}.png`;
      this.mediaModeData.face.fallBackSrc = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/0_${this.headerPlayer.male === false ? 'f' : 'm'}.png`;
      this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
      this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
    },
    async loadSeasonsClient() {
      let seasonsClientResponse = await ClientService.getTinySeasonsOfClient(
        this.currentUser.clientId
      );

      this.seasonsClient = _.chain(seasonsClientResponse.data).filter((s) => {return this.player.isMale ? s.m : !s.m}).value();
    
      //If user has only physical radars
      if(this.onlyPhysicalData){
        //Clear seasons to get just seasons with physical data
        this.seasonsClient = this.seasonsClient.filter(s=> s.hasPD);
        this.seasonsActiveObj = this.seasonsClient;
      }else{

        this.seasonsActiveObj = _.chain(this.seasonsClient)
        .filter(s => {
          let top5 = this.player.isMale ? [1, 1923, 7, 4129, 7662] : [101646,101643,101644,101647,101645];
          let isDefault =
            _.includes(top5, s.lId) && s.sY === s.lY;
          return isDefault;
        }).value();

        if (!this.seasonsActiveObj.length) {
            this.seasonsActiveObj = this.seasonsClient.filter(s => s.id === this.currentUserSeasonId);

            if (!this.seasonsActiveObj.length) {
              //If user has no team seasonId or league seasonId, take player season to compare season
              this.seasonsActiveObj = this.seasonsClient.filter(s => s.id === this.seasonModelObj.seasonId);
            }
        }
      }

      this.seasonsActive = _.map(this.seasonsActiveObj,(s) => s.id);
      //Duplicate season objects to allow filter seasons on physical template
      //checked and preserve all seasons client
      this.seasonsActiveFiltered = this.onlyPhysicalData ? this.seasonsClient.map(s => s.id) : this.seasonsActive;
      this.seasonsClientFiltered = this.seasonsClient;
      this.seasonsActiveObjFiltered = this.seasonsActiveObj;
      let maxCurrentMatchDay = Math.max(...this.seasonsActiveObjFiltered.map(s=> s.cMD));
      this.minutes[0] = Math.min(450,maxCurrentMatchDay*50);
    },
    async loadPositionsPlayed() {
      let seasonSelected = _.find(
        this.playerSeasonData,
        s => s.seasonId === this.seasonModel
      );

      //Get positions played
      let [positionsData, shortPositionsData] = await Promise.all([
        PlayerService.getPositionsPlayedOfPlayer(
          this.playerId,
          seasonSelected.teamId,
          seasonSelected.seasonId
        ),
        PlayerService.getShortPositionsPlayedOfPlayer(
          this.playerId,
          seasonSelected.teamId,
          seasonSelected.seasonId
        ),
      ]);

      //Positions Played used in getRadarTemplates method
      this.positionsPlayed = positionsData.data.filter(
        (p) => p.minutesPlayed != 0
      );
      this.shortPositionsPlayed = shortPositionsData.data;
      
      this.allShortPositions = this.allShortPositions.map(item => {
        const pos = this.shortPositionsPlayed.find(sp => sp.fieldPositionId === item.fieldPositionId);
        return pos ? pos : item;
      });

      
      this.shortPositionsPlayedModel = this.shortPositionsPlayed.slice(0,6).map(
        (p) => p.fieldPositionId
      );
    
    },
    async loadTemplates(){
      //ids from dim_category for arrigo and physical categories
      const physicalCategoryId = 17;
      const arrigoCategoryId = 26;
      
      let positionIds = [...new Set(this.positionsPlayed.map(p => p.positionId))];
      let response = await PlayerRadarService.getRadarTemplatesByPositionsAndClientId(positionIds);
      let driblabRadars = _.filter(response.data, r => {
        return (r.clientId === 0 && [0,18].includes(r.categoryId));
      });
      
      //Get all physical radars, includes physical custom just for users that have ROLE_PHYSICAL_DATA
      let driblabPDRadars = this.showPhysicalTemplates ? _.filter(response.data, r => {
        return r.clientId === 0 && physicalCategoryId == r.categoryId;
      }) : [];

      //Get all arrigo radars, just for users that have ROLE_ARRIGO
      let driblabArrigoRadars = this.showArrigoTemplates ? _.filter(response.data, r => {
        return r.clientId === 0 && arrigoCategoryId == r.categoryId;
      }) : [];

      //Get client radars, without physical
      let clientRadars = _.filter(response.data, r => {
        return r.clientId === this.currentUser.clientId;
      });

      let mediaRadars = _.filter(response.data, r => {
        return (r.categoryId === 38 && ((this.currentUser.clientType === 'Driblab') || this.currentUser.clientType === 'Media'));
      });

      let minutesAccum = _.reduce(
        this.positionsPlayed,
        (accum, p) => {
          let template = p.position;
          if (!accum[template]) {
            accum[template] = {};
          }
          accum[template] = { positionId: p.positionId, mins: p.minutesPlayed };
          return accum;
        },
        {}
      );

      let finalRadars;
      if (this.userHasRole('ROLE_ADMIN')) {
        if (_.size(clientRadars) > 0) finalRadars = [...clientRadars,...driblabRadars];
        else finalRadars = [...driblabRadars];
      } else {
        finalRadars = _.size(clientRadars) > 0 ? [...clientRadars,...driblabRadars] : [...driblabRadars];
      };

      let radarsAllowed = _.concat(finalRadars, mediaRadars, driblabPDRadars, driblabArrigoRadars);
      //If user has only physical radars
      if(this.onlyPhysicalData){
        //If only can see Physical Data, show Driblab Physical templates and radars that contains at least one physical stat
        radarsAllowed = _.concat(driblabPDRadars, clientRadars.filter(r => r.cPD));
      }

      let publicRadars = radarsAllowed.filter(r => r.visibilityId === 1);
      let privateRadars = radarsAllowed.filter(r => r.visibilityId === 2 && r.createdByUserId === this.currentUser.id);
      let supervisedRadars = getSupervisedItems(radarsAllowed.filter(r => r.visibilityId === 3), this.currentUser.userType, this.currentUser.id, this.currentUser.clientId);

      radarsAllowed = [...publicRadars,...privateRadars,...supervisedRadars];

      this.templates = _.chain(radarsAllowed)
        .filter(r => r.visible)
        .map((value, key) => {
          let mins = _.reduce(minutesAccum,(accum,ma) => ma.positionId === value.positionId ? accum + ma.mins : accum,0);
          return {
            radarId: value.radarId,
            positionId: value.positionId,
            categoryId: value.categoryId,
            minutesPlayed: mins,
            isMedia: _.includes(value.code,"Media") && this.mediaMode,
            template: value.code,
            clientId: value.clientId,
            defaultRadar: value.defaultRadar,
            radarName: value.radarName,
            //Contains Eventing stats
            cXY: value.cXY,
            //Contains Physical stats
            cPD: value.cPD,
            //Contains Arrigo stats
            cA: value.cA
          };
        })
        .uniq()
        .orderBy(["minutesPlayed","isMedia"],["desc","desc"])
        .value();

      let templateActiveObject = this.getDefaultTemplate();

      this.templateActiveId = templateActiveObject.radarId;
    },
    getDefaultTemplate(){
     //1.- Get radar with is_default = true (on dim_radar)
      let defaultTemplates = this.templates.filter(t => {
        return t.defaultRadar;
      });
      //If there is a default template, return
      if(defaultTemplates.length)
        return defaultTemplates[0];

      //Check if template contains PD or Arrigo and season selected has PD or Arrigo
      let seasonSelected = _.find(
        this.playerSeasonData,
        s => s.seasonId === this.seasonModel
      );
      
      return this.templates.find(t => 
                                (seasonSelected.hasXY && t.cXY) || 
                                (seasonSelected.hasPhysicalData && t.cPD) ||
                                (seasonSelected.hasArrigoData && t.cA));
    },
    async onPlayerSeasonChange() {
      this.loading = true;
      this.seasonModelObj = this.playerSeasonData.find(s => s.seasonId === this.seasonModel);
      this.physicalTemplateDisabled = !this.seasonModelObj.hasPhysicalData;
      this.arrigoTemplateDisabled = !this.seasonModelObj.hasArrigoData;
      this.xyTemplateDisabled = !this.seasonModelObj.hasXY;
      
      this.$forceUpdate();
      await this.loadPositionsPlayed();
       //Get all available templates by clientId
      await this.loadTemplates();
      await this.loadStats();
      await this.loadBeeswarmData();
      this.loadAllStatsBeeswarmData();
      this.onFilterSelectAllClick();
      setTimeout(() => { this.onChangeInput(true);}, 100);
    },
    async onTemplateChange() {
      this.loading = true;
      this.physicalTemplateSelected = this.templates.find(t => t.radarId === this.templateActiveId).cPD;
      if(this.physicalTemplateSelected){
        //Filter seasons to get just seasons with physical data
        this.seasonsClientFiltered = this.seasonsClient.filter(s=> s.hasPD);
        //If client has more than 5 PD seasons, get top 5, if hasn't hired, get 5 first 
        if(this.seasonsClientFiltered.length > 5){
          this.seasonsActiveObjFiltered = _.chain(this.seasonsClientFiltered)
          .filter(s => {
            let top5 = this.player.isMale ? [1, 1923, 7, 4129, 7662] : [101646,101643,101644,101647,101645];
            let isDefault =
              _.includes(top5, s.lId) && s.sY === s.lY;
            return isDefault;
          }).value();

          if (!this.seasonsActiveObjFiltered.length) {
              this.seasonsActiveObjFiltered = this.seasonsClientFiltered.slice(0,5);
          }
        }else{
          //If after filter by PD, client has 5 or less, then get all 
          this.seasonsActiveObjFiltered = this.seasonsClientFiltered;
        }
      }

      this.seasonsActive = this.seasonsActiveObjFiltered.map((s) => s.id);
      this.seasonsActiveFiltered = this.onlyPhysicalData ? this.seasonsClientFiltered.map(s => s.id) : this.seasonsActive;
      
      this.$forceUpdate();
      await this.loadStats();
      await this.loadBeeswarmData();
      this.loadAllStatsBeeswarmData();
      setTimeout(() => { this.onChangeInput(true);}, 100);
    },
    getPositionsByTemplate(templateId) {
      let positionIdOfTemplateActive = this.templates.find(t => t.radarId === templateId).positionId;
      return getShortPositionsByTemplate(positionIdOfTemplateActive, false);
    },
    async loadAllStatsBeeswarmData(){
      this.seasonModelObj = this.playerSeasonData.find(s => s.seasonId === this.seasonModel);
      let templateActiveObject = this.templates.find(t => t.radarId === this.templateActiveId);
      this.beeswarmAllStatsData = await BeeswarmService.getBeeswarm(
        this.playerId,
        this.seasonModelObj.teamId,
        this.seasonModelObj.seasonId,
        templateActiveObject.radarId,
        this.seasonsActiveFiltered
      );
      if (this.beeswarmAllStatsData && this.beeswarmAllStatsData.stats.length) {
        this.beeswarmAllStatsData.stats = this.beeswarmAllStatsData.stats.map(s => {
          s.product = this.getProductInfoById(s.pId, this.currentUserLanguage);
          s.name = this.$t(`statsNaming.${s.code}`);
          s.tooltip = this.$t(`statsNaming.description.${s.code}`);
          s.subtitle = this.$t('statsNaming.subtitles.' + s.code);
          return s;
        });    
      };
      //If mediaMode is clicked, get again 3 first stats from template
      if(this.mediaMode && this.beeswarmAllStatsData){
        this.mediaModeData.stats = _.take(this.beeswarmAllStatsData.stats, 3).map(s => s.code);
      }
    },
    async loadBeeswarmData(){
      //Reset all stats data
      this.beeswarmAllStatsData = [];
      this.beeswarmAllStatsData.length = 0;
      this.seasonModelObj = this.playerSeasonData.find(s => s.seasonId === this.seasonModel);
      let templateActiveObject = this.templates.find(t => t.radarId === this.templateActiveId);
   
      this.beeswarmDataByCatgories = await BeeswarmService.getBeeswarmByCategory(
        this.playerId,
        this.seasonModelObj.teamId,
        this.seasonModelObj.seasonId,
        templateActiveObject.radarId,
        this.selectedCategories[0].id,
        this.seasonsActiveFiltered
      );

    },
    setSize() {
      if (this.$refs && this.$refs.beeswarm) {
        let sizeMultiplier = this.$vuetify.breakpoint.mobile ? 1 : 2; //0.67 - 1.745
        this.size = this.$refs.beeswarm.clientWidth / sizeMultiplier - this.padding;// minus padding
      }
    },
    onWindowResize(event) {
      //If width or height has changed, then reload beeswarm
      if(this.prevWindowWidth != document.documentElement.clientWidth || this.prevWindowHeight != document.documentElement.clientHeight){
        this.loading = true;
        this.prevWindowWidth = document.documentElement.clientWidth;
        this.prevWindowHeight = document.documentElement.clientHeight;
        setTimeout(() => {this.setSize(); this.onChangeInput(true)}, 1);
      }
    },
    //Do timeOut if need to go Back to get Data, don't do if just filter on the frontend
    onChangeInput(doTimeOut, force = true) {
      this.doTimeOut = doTimeOut;
      this.idKey++;
      this.$forceUpdate();

      if(this.$vuetify.breakpoint.mobile && !this.mediaMode && this.$refs.beeswarmRef && this.$refs.beeswarmRef.drawBeeswarm){
              this.$refs.beeswarmRef.drawBeeswarm(true);
      }else{
        _.forEach(this.$refs.beeswarmRef,(r) => r.drawBeeswarm(force))
      }

      if(this.$refs.playerAvatarMediaRef){
        this.$refs.playerAvatarMediaRef.printD3(true);
      }

      if(this.$refs.driblabSocialMediaRef){
        this.$refs.driblabSocialMediaRef.printD3(true);
      }

      setTimeout(() => (this.loading = false), 100);
    },
    getRadioStyle(template) {
      const isDisabled = (this.xyTemplateDisabled && template.cXY) || (this.physicalTemplateDisabled && template.cPD) || (this.arrigoTemplateDisabled && template.cA);
      return getRadioStyle(template, isDisabled);
    },
    async saveAsPNG() {
      let parent = this.mediaMode ? this.prepareSvgForMedia() : this.joinBeeswarms();
      let font = await loadFonts(this.mediaMode ? this.mediaModeData.fontFamily : 'NeueMontreal');
      copyAndAppendElement(parent,font.node(),0,0);
      const fileName = "Driblab_Beeswarm_" + this.playerSeasonDataCurrentYear[0].playerName.replace(' ', '_') + ".png";
      const isDarkMode = this.$vuetify.theme.dark;
      const bgColor = isDarkMode ? '#2B2B2B' : '#FFFFFF';
      const textColor = isDarkMode ? '#FFFFFF' : '#000000';
      downloadPng(parent, fileName, true, bgColor, textColor);
    },
    async saveAsSVG() {
      let parent = this.mediaMode ? this.prepareSvgForMedia() : this.joinBeeswarms();
      let font = await loadFonts(this.mediaMode ? this.mediaModeData.fontFamily : 'NeueMontreal');
      copyAndAppendElement(parent,font.node(),0,0);
      const fileName = "Driblab_Beeswarm_" + this.playerSeasonDataCurrentYear[0].playerName.replace(' ', '_');
      const isDarkMode = this.$vuetify.theme.dark;
      const bgColor = isDarkMode ? '#2B2B2B' : '#FFFFFF';
      const textColor = isDarkMode ? '#FFFFFF' : '#000000';
      exportSvg(parent, fileName, true, bgColor, textColor);
    },
    joinBeeswarms(){
      let parent = document.createElementNS('http://www.w3.org/2000/svg','svg');
      let rowCount = 0;
      let heightAccum = 0;
      let yOffset = 160;
      let rowGap = 90;
      let textPadding = 45;
      
      let out = parent.appendChild(document.createElement("defs"))
                    .appendChild(document.createElement("style"))
      out.setAttribute('type', 'text/css')
      out.appendChild(document.createTextNode(this.getBeeswarmStyles()));
      parent.style.background = this.darkMode ? "#2a2a2a" : "none"

      //Prepare headers texts
      let header = document.createElementNS('http://www.w3.org/2000/svg','g');
      header.setAttribute("x",8);
      header.setAttribute("y",yOffset)
      let title = document.createElementNS('http://www.w3.org/2000/svg','text');
      title.style["font-family"] = "NeueMontreal"
      title.style["font-size"] = "32px"
      title.style["color"] = this.darkMode ? "white" : "black";
      title.style["fill"] = "currentColor"
      title.setAttribute("x",8);
      title.setAttribute("y",40)
      title.appendChild(document.createTextNode(this.playerSeasonDataCurrentYear[0].playerName));
      header.appendChild(title);
      let subtitle = document.createElementNS('http://www.w3.org/2000/svg','text');
      let season = _.find(this.playerSeasonData,(d) => d.seasonId === this.seasonModel);
      subtitle.style["font-family"] = "NeueMontreal"
      subtitle.style["font-size"] = "24px"
      subtitle.style["color"] = this.darkMode ? "white" : "black"
      subtitle.style["fill"] = "currentColor"
      subtitle.setAttribute("x",9);
      subtitle.setAttribute("y",70)
      subtitle.appendChild(document.createTextNode(season.seasonName + " · "  + season.teamName));
      header.appendChild(subtitle);
      let headerNote = document.createElementNS('http://www.w3.org/2000/svg','text');
      headerNote.style["font-family"] = "NeueMontreal"
      headerNote.style["font-size"] = "16px"
      headerNote.style["color"] = this.darkMode ? "white" : "black"
      headerNote.style["fill"] = "currentColor"
      headerNote.setAttribute("x",10);
      headerNote.setAttribute("y",95)
      headerNote.appendChild(document.createTextNode(this.$t("beeswarm.per90")));
      header.appendChild(headerNote);
      parent.appendChild(header)

      let statsArray = this.beeswarmDataFiltered.stats.map(s => s.code);
      _.forEach(statsArray, (stat, index) => {
        let dims = document.querySelector(`#beeswarmSvg-${stat}`).getBoundingClientRect();
        let copy = document.querySelector(`#beeswarmSvg-${stat}`).cloneNode(true);
        
        // Put warning text in white or black according to the bg theme
        let innerTexts = copy.querySelectorAll('text');
        innerTexts.forEach(textNode => {
          if (!textNode.style["fill"] || textNode.style["fill"] === "black" || textNode.getAttribute("fill") === "#000000") {
            textNode.style["fill"] = this.darkMode ? "#888888" : "#555555"; 
          }
        });

        let x = (index % 2) === 1 ? dims.width : 0;
        let rowY = (dims.height * rowCount) + (rowCount * rowGap) + yOffset + 20;
        if ((index % 2) === 1 || _.size(statsArray) === 1) {
          rowCount++;
        }
        if ((index % 2) === 0 || _.size(statsArray) === 1) {
          heightAccum += dims.height + rowGap;
        }

        // Create SVG text element for stat title
        const statTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        statTitle.style["font-family"] = "NeueMontreal";
        statTitle.style["font-size"] = "20px";
        statTitle.style["fill"] = this.darkMode ? "white" : "black";
        statTitle.setAttribute("x", x + 10);  
        statTitle.setAttribute("y", rowY); 
        statTitle.appendChild(document.createTextNode(this.$t('statsNaming.' + stat))); 

        // Create SVG text element for stat subtitle
        const statSubtitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        statSubtitle.style["font-family"] = "NeueMontreal";
        statSubtitle.style["font-size"] = "14px"; 
        statSubtitle.style["fill"] = this.darkMode ? "#bbbbbb" : "#555555"; 
        statSubtitle.setAttribute("x", x + 10);
        statSubtitle.setAttribute("y", rowY + 20);
        statSubtitle.appendChild(document.createTextNode(this.$t('statsNaming.subtitles.' + stat)));

        // Append elements
        parent.appendChild(statTitle);
        parent.appendChild(statSubtitle);
        
        copy.setAttribute("x", x);
        copy.setAttribute("y", rowY + textPadding);
        parent.appendChild(copy);
      });
      
      parent.setAttribute("width", this.size * 2);
      parent.setAttribute("height", heightAccum + yOffset + (rowCount * textPadding));
      return parent;
    },
    prepareSvgForMedia(){
      let parent = document.createElementNS('http://www.w3.org/2000/svg','svg');
      let heightAccum = 0;
      let widthAccum = 50;
      let yOffset = 120;
      let out = parent.appendChild(document.createElement("defs"))
                    .appendChild(document.createElement("style"))
      out.setAttribute('type', 'text/css')
      out.appendChild(document.createTextNode(this.getBeeswarmStyles()));
      parent.style.background = this.mediaModeData.bgColor;

      let startX = (-this.avatarSize.width/2)+(this.chartSize.width/1.55);

      copyAndAppendElement(
        parent,
        document.querySelector(`#playerAvatarMedia${this.playerId}SVG`),
        this.mediaModeData.comparisonPlayer ? 25 : startX,
        0
      );
      heightAccum += (this.avatarSize.height * 1.1);
      widthAccum += this.avatarSize.width;

      _.forEach(this.mediaModeData.stats, (stat, index) => {
        let beeswarmElement = document.querySelector(`#beeswarmSvg-${stat}`);
        if (!beeswarmElement) {
          console.warn(`SVG element for stat ${stat} not found!`);
          return;
        }

        let dims = beeswarmElement.getBoundingClientRect();
        let copy = beeswarmElement.cloneNode(true);

        const statTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        statTitle.setAttribute("x", 25);
        statTitle.setAttribute("y", heightAccum + 25);
        statTitle.setAttribute("fill", this.darkMode ? "white" : "black");
        statTitle.setAttribute("font-family", this.mediaModeData.fontFamily || "NeueMontreal");
        statTitle.setAttribute("font-size", "20px");
        statTitle.textContent = this.$t('statsNaming.' + stat);
        parent.appendChild(statTitle);

        const statSubtitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        statSubtitle.setAttribute("x", 25);
        statSubtitle.setAttribute("y", heightAccum + 45);
        statSubtitle.setAttribute("fill", this.darkMode ? "#bbbbbb" : "#555555");
        statSubtitle.setAttribute("font-family", this.mediaModeData.fontFamily || "NeueMontreal");
        statSubtitle.setAttribute("font-size", "14px");
        statSubtitle.textContent = this.$t('statsNaming.subtitles.' + stat);
        parent.appendChild(statSubtitle);

        copyAndAppendElement(parent, copy, 25, heightAccum);
        heightAccum += dims.height;
      });

      if(this.mediaModeData.socialMedia){
        let socialMediaElement = document.querySelector(`#driblabSocialMedia${this.playerId}SVG`);
        if (socialMediaElement) {
          let dims = socialMediaElement.getBoundingClientRect();
          copyAndAppendElement(parent, socialMediaElement, 25, heightAccum);
          heightAccum += 50;
        }
      }

      parent.setAttribute("width", this.chartSize.width + 50);
      parent.setAttribute("height", heightAccum);
      return parent;
    },
    getBeeswarmStyles(){
      let fontFamily = this.mediaMode ? this.mediaModeData.fontFamily : 'NeueMontreal';
      return `.axis path,
      .axis line {
        fill: none;
        stroke: #000;
        shape-rendering: crispEdges;
      }
      .axis text {
        font: 10px sans-serif;
      }
      .label {
        font-size: 0.85rem;
        fill: #000;
        font-family: ${fontFamily}
      }
      .label-dark {
        font-size: 0.85rem;
        fill: #fff;
        font-family: ${fontFamily}
      }
      .axisDark path,
      .axisDark line {
        fill: none;
        stroke: white;
        shape-rendering: crispEdges;
      }
      .axisDark text {
        font: 10px sans-serif;
        fill: white;
      }
      .cells path {
        fill: none;
        pointer-events: all;
      }
      .cells :hover circle {
        fill: red;
      }
      .shadow {
        text-shadow: 3px 3px 3px #fff, -3px -3px 3px #fff, -3px 3px 3px #fff,
          3px -3px 3px #fff, -3px 0px 3px #fff, 3px 0px 3px #fff;
      }`
    },
    onMediaModeDataChange(event){
      this.mediaModeData = event;
      setTimeout(() => {this.onChangeInput(false);},100)
    },
    onMediaModeChange(event){
      this.mediaMode = event;
      if(this.mediaMode && this.beeswarmAllStatsData){
        this.mediaModeData.stats = _.take(this.beeswarmAllStatsData.stats, 3).map(s => s.code);
      }
    },
    onBeeswarmSizeChange(size){
      this.chartSize = size;
    },
    //Load custom seasons templates
    async loadSeasonLists(){
      let seasonResponse = await ClientService.loadSeasonList();
      let clientSeasonsList = _.filter(seasonResponse.data,l => {
        return _.every(l.seasonIds,(id) => _.chain(this.seasonsClient).map(s => s.id).includes(id).value())
      });
      let publicSeasons = clientSeasonsList.filter(sl => sl.visibilityId === 1);
      let privateSeasons = clientSeasonsList.filter(sl => sl.visibilityId === 2 && sl.createdByUserId === this.currentUser.id);
      let supervisedSeasons = getSupervisedItems(clientSeasonsList.filter(sl => sl.visibilityId === 3), this.currentUser.userType, this.currentUser.id);

      this.seasonLists = [...publicSeasons,...privateSeasons,...supervisedSeasons];
      this.seasonsClient = [...this.seasonLists, {divider:true}, ...this.seasonsClient];
      this.seasonsClientFiltered = this.seasonsClient;
    },
    onChangeSeasonInput(event){
      this.beeswarmLoaded = 0;
      let lastSelected = _.last(event);
      if(lastSelected && lastSelected.seasonIds){
        this.seasonsActiveFiltered = [...lastSelected.seasonIds];
        this.seasonsActiveObjFiltered = _.filter(this.seasonsClientFiltered,(s) => _.includes(this.seasonsActiveFiltered,s.id));
      }
      this.$forceUpdate();
    },
    async onBlurSeasonInput(event){
      this.loading = true;
      this.seasonsActiveFiltered = _.map(this.seasonsActiveObjFiltered,(s) => s.id);
      await this.loadBeeswarmData();
      this.loadAllStatsBeeswarmData();
      this.$forceUpdate();
      setTimeout(() => { this.onChangeInput(true);}, 100);
    },
    onBeeswarmDataLoaded(data,stat){
      if(this.mediaMode){
        let comparisonField = _.find(this.mediaModeFields,(f) => f.key === "comparisonPlayer");
        comparisonField.options = _.chain(data).map(d => {d.playerId = d.id;return d;}).orderBy(data,'name').value();
      }

      this.beeswarmLoaded++;

      let allStats = this.mediaMode ? this.mediaModeData.stats : this.beeswarmDataFiltered.stats.map(s => s.code);
      if(allStats && allStats.length === this.beeswarmLoaded){
        UserService.putLastSearchRendered({id:this.lastSearchId});
      }

      this.$forceUpdate();
    },
    async onGenerateIFrameClick(){
      this.loadingExport = true;
      let elToExport = this.mediaMode ? this.prepareSvgForMedia() : this.joinBeeswarms();
      let font = await loadFonts(this.mediaMode ? this.mediaModeData.fontFamily : 'NeueMontreal');
      copyAndAppendElement(elToExport,font.node(),0,0);
      let key = v4();
      key = `${key}.json`;
      let chartObject = {
        awsKey:key,
        tool: 'beeswarm',
        category: 'player',
        componentId: this.playerId,
        width: elToExport.getAttribute("width"),
        height: elToExport.getAttribute("height")
      }
      let postResponse = await UserChartService.postChart(chartObject)
      let chartEntity = postResponse.data;

      this.$refs.chartExportLinksModalRef.open(chartEntity);
      this.loadingExport = false;

      let urlResponse = await UserChartService.getUploadURL(chartEntity.id,key);
      let uploadUrl = urlResponse.data;
      var jsonse = JSON.stringify({
        size: Number(chartEntity.width),
        height: Number(chartEntity.height),
        player:this.player,
        seasonId: this.seasonModel,
        data: this.beeswarmDataFiltered,
        seasonIds: this.seasonsActiveFiltered,
        positions:this.shortPositionsPlayedModel,
        height:this.height,
        minutes:this.minutes,
        age: this.age,
        language: this.currentUserLanguage,
        darkMode: this.darkMode,
        parentSVG: this.$refs.parentSVGRef,
        mediaModeData: this.mediaModeData,
        mediaMode: this.mediaMode,
        idKey: this.idKey,
        playerAvatarKey: 0,
        key: 1,
        socialMediaKey: 2,
        chartType:"beeswarm"
      });
      var blob = new Blob([jsonse], {type: "application/json"});

      let uploadResponse = await DocumentService.upload(uploadUrl,blob);
      this.postSearchChart(chartEntity);
    },
    async postSearchChart(chart){
      const authorized = true;
      let metaInfo = null;
      let category = "player";
      let urlId = this.playerId;

      metaInfo = JSON.stringify({
        link: `${API_URL}${chart.id}/key/${chart.awsKey}/compId/${chart.componentId}/catId/${chart.categoryId}/tId/${chart.toolId}`
      });
      const tool = 'embed';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },
    getPlayersToHighlight(data) {
      //If excluded teams, filter its players
      if (this.excludeTeamsModel.length) this.playersOnBeeswarm = data.filter(p => !this.excludeTeamsModel.includes(p.teamId));
      else this.playersOnBeeswarm = data;
      this.playersOnBeeswarm = _.orderBy(this.playersOnBeeswarm, 'tN', 'asc');
    },
    getRadarTemplateSubtitle(groupKey){
      return getRadarTemplateSubtitle(groupKey, this.currentUserLanguage);
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
    onImageLoadFailure(event){
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
    onExcludeTeamsClick() {
      this.filterTeamsModel = this.teamsOnBeeswarm.filter(t => !this.excludeTeamsModel.includes(t.id)).map(t => t.id);
      this.onChangeInput(false);
    },
    onFilterTeamsClick() {
      this.excludeTeamsModel = this.teamsOnBeeswarm.filter(t => !this.filterTeamsModel.includes(t.id)).map(t => t.id);
      this.onChangeInput(false);
    },
    onExcludedSelectAllClick() {
      this.excludeTeamsModel = this.teamsOnBeeswarm.map(t => t.id);
      this.onExcludeTeamsClick();
    },
    onFilterSelectAllClick() {
      this.filterTeamsModel = this.teamsOnBeeswarm.map(t => t.id);
      this.onFilterTeamsClick();
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    onNewTemplateClick(){
      this.$refs.formModalRef.open();
    },
    getProductInfoById(id, lang) {
      return getProductInfoById(id, lang);
    },
  },
  destroyed(){
    this.mediaModeData = null;
    this.mediaModeFields = null;
    this._props = null;
    this._watcher = null;
    this._watchers = null;
    this.$options.propsData = null;
    this.$parent = null;
    this._self = null;
    this.$children = null;
    this.$refs = null;
    this._refs = null;
  }
};
</script>

<style>
  .v-expansion-panel--active {
      min-height: 48px !important;
  }
  .v-expansion-panel-header {
      min-height: 36px !important;
      padding: 8px 12px !important
  }

  .axis path,
  .axis line {
    fill: none;
    stroke: currentColor;
    shape-rendering: crispEdges;
  }
  .axis text {
    font: 10px sans-serif;
    fill: currentColor;
  }

  .axisDark path,
  .axisDark line {
    fill: none;
    stroke: currentColor;
    shape-rendering: crispEdges;
  }
  .axisDark text {
    font: 10px sans-serif;
    fill: currentColor;
  }

  .cells path {
    fill: none;
    pointer-events: all;
  }

  .cells :hover circle {
    fill: red;
  }

  .label {
    font-size: 0.85rem;
    fill: #000;
    font-size: 10px;
  }
  .label-dark {
    font-size: 0.85rem;
    fill: #fff;
    font-size: 10px;
  }

  .shadow {
    text-shadow: 3px 3px 3px #fff, -3px -3px 3px #fff, -3px 3px 3px #fff,
      3px -3px 3px #fff, -3px 0px 3px #fff, 3px 0px 3px #fff;
  }
  .shadow-dark {
    text-shadow: 3px 3px 3px #000, -3px -3px 3px #000, -3px 3px 3px #000,
      3px -3px 3px #000, -3px 0px 3px #000, 3px 0px 3px #000;
  }

  div.tooltip {
    font-family: 'NeueMontreal';
    position: absolute;
    display: none;
    pointer-events: none;
    text-align: center;
    color: #081f2c;
    width: 225px;
    height: 115px;
    padding: 2px;
    font-size: 14px;
    background: none repeat scroll 0 0 #ffffff;
    border-radius: 4px;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.15);
  }
  div.tooltip:after {
    box-sizing: border-box;
    display: block;
    font-size: 15px;
    line-height: 3.3;
    color: #000000;
    content: "\25BC";
    text-align: center;
  }

  div.tooltipDark {
    font-family: 'NeueMontreal';
    position: absolute;
    display: none;
    pointer-events: none;
    text-align: center;
    color: #081f2c;
    width: 225px;
    height: 115px;
    padding: 2px;
    font-size: 14px;
    background: none repeat scroll 0 0 #ffffff;
    border-radius: 4px;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.15);
  }
  div.tooltipDark:after {
    box-sizing: border-box;
    display: block;
    font-size: 15px;
    line-height: 3.3;
    color: #ffffff;
    content: "\25BC";
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/views/player/PlayerBeeswarm.vue